'use client';

import {
  Avatar,
  Dropdown,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
} from 'flowbite-react';
import { Code2, LogOut, MessageCircle, User } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useHasRole } from '@/hooks/useCurrentUserRoles';
import { customAvatarTheme, customDropdownTheme } from '@/lib/flowbite-theme';
import ShowIfAuthenticated from '../auth/showIfAuthenticated';

export default function UserDropdown({
  user,
  nickname,
  isMobile,
  profileImageUrl,
}: {
  // Auth0 user object - can be null/undefined from useUser()
  user?: {
    email?: string;
    id?: string;
    name?: string;
    image?: string;
  } | null;
  nickname?: string;
  isMobile?: boolean;
  profileImageUrl?: string | null;
}) {
  // Check if user has Sax role for DreamChat access
  // Also track loading state to prevent the link from flashing during initial role load
  const { hasRole: isSaxUser, isLoading: isRolesLoading } = useHasRole('Sax');
  const { hasRole: hasPartnerApi, isLoading: isPartnerApiLoading } = useHasRole('Partner API');

  // Track the currently displayed image to prevent flashing during loads
  const [displayedImage, setDisplayedImage] = useState<string | undefined>(
    undefined,
  );
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Preload the profile image before displaying it
  useEffect(() => {
    const imageToLoad = profileImageUrl || user?.image;

    if (!imageToLoad) {
      setDisplayedImage(undefined);
      setIsImageLoaded(false);
      return;
    }

    // If the image is already displayed, don't reload it
    if (displayedImage === imageToLoad && isImageLoaded) {
      return;
    }

    // Preload the image
    const img = new Image();
    img.onload = () => {
      setDisplayedImage(imageToLoad);
      setIsImageLoaded(true);
    };
    img.onerror = () => {
      // If image fails to load, fall back to showing nothing (will show initials)
      setDisplayedImage(undefined);
      setIsImageLoaded(false);
    };
    img.src = imageToLoad;
  }, [profileImageUrl, user?.image, displayedImage, isImageLoaded]);

  const handleSignOut = async () => {
    console.log('[LOGOUT DEBUG] Current user session:', user);

    try {
      console.debug(`[LOGOUT DEBUG] Attempting logout for user: ${user?.id}`);

      window.location.href = '/auth/logout';
    } catch {
      // Fallback to regular logout on error
      window.location.href = '/auth/logout';
    }
  };

  const displayName = user?.name || nickname || 'User';
  const userInitials = displayName.charAt(0).toUpperCase();
  const userEmail = user?.email || '';

  return (
    <div>
      <Dropdown
        arrowIcon={false}
        inline
        label={<span className="sr-only">User menu for {displayName}</span>}
        renderTrigger={() => (
          <button
            aria-label={`User menu for ${displayName}`}
            className="flex items-center focus:outline-none focus:ring-2 focus:ring-purple-500 rounded-full"
            type="button"
          >
            <Avatar
              alt={`Profile picture for ${displayName}`}
              className="cursor-pointer"
              img={displayedImage}
              placeholderInitials={userInitials}
              rounded
              status="online"
              statusPosition="bottom-right"
              theme={customAvatarTheme}
            />
          </button>
        )}
        theme={customDropdownTheme}
      >
        <ShowIfAuthenticated>
          <DropdownHeader>
            <div className="min-w-[200px]">
              <span className="block truncate text-sm font-semibold text-gray-900 dark:text-white tracking-tight">
                {userEmail}
              </span>
            </div>
          </DropdownHeader>
          <Link href="/profile">
            <DropdownItem className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>Profile</span>
            </DropdownItem>
          </Link>
          {/* DreamChat - Only visible to Sax users, hide while loading to prevent flash */}
          {!isRolesLoading && isSaxUser && (
            <Link href="/dreamchat">
              <DropdownItem className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                <span>DreamChat</span>
              </DropdownItem>
            </Link>
          )}
          {/* API Docs - Only visible to Partner API role holders */}
          {!isPartnerApiLoading && hasPartnerApi && (
            <Link href="/api-docs">
              <DropdownItem className="flex items-center gap-2">
                <Code2 className="h-4 w-4" />
                <span>API Docs</span>
              </DropdownItem>
            </Link>
          )}
          {/* <DropdownItem as={Link} href="/settings">
            Settings
          </DropdownItem> */}
          <DropdownDivider />
          <DropdownItem
            className="flex items-center gap-2"
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </DropdownItem>
        </ShowIfAuthenticated>
      </Dropdown>
    </div>
  );
}
